import { computed, ref } from 'vue'
import {
  deleteSellerProduct,
  fetchSellerProducts,
  setSellerProductStatus,
  setSellerProductStock,
  type MallProductStatus,
  type ServerMallProduct,
} from '@/api/mallProduct'

/**
 * 판매업체가 등록한 실제 상품 목록.
 * 목록 화면과 등록 폼이 같은 상태를 보도록 모듈 스코프에 둔다.
 */
const products = ref<ServerMallProduct[]>([])
const loading = ref(false)
const loaded = ref(false)
const errorMessage = ref('')

let inFlight: Promise<void> | null = null
let loadSeq = 0

export function useSellerProducts() {
  const load = async (refresh = false) => {
    if (!refresh && (loaded.value || inFlight)) {
      await inFlight
      return
    }

    const seq = ++loadSeq
    loading.value = true
    errorMessage.value = ''

    const task = fetchSellerProducts()
      .then((list) => {
        if (seq !== loadSeq) return
        products.value = list
        loaded.value = true
      })
      .catch((error: unknown) => {
        if (seq !== loadSeq) return
        /* 백엔드가 아직 안 올라왔을 수도 있다. 목 데이터만으로도 화면은 살아 있어야 한다. */
        errorMessage.value = error instanceof Error ? error.message : '상품을 불러오지 못했습니다.'
        products.value = []
      })
      .finally(() => {
        if (seq !== loadSeq) return
        loading.value = false
        inFlight = null
      })

    inFlight = task
    await task
  }

  const upsert = (product: ServerMallProduct) => {
    const index = products.value.findIndex((item) => item.code === product.code)
    if (index >= 0) {
      products.value.splice(index, 1, product)
      return
    }
    products.value.unshift(product)
  }

  const changeStatus = async (code: string, status: MallProductStatus) => {
    await setSellerProductStatus(code, status)
    const target = products.value.find((item) => item.code === code)
    if (target) {
      target.status = status
    }
  }

  const changeStock = async (code: string, stock: number) => {
    await setSellerProductStock(code, stock)
    const target = products.value.find((item) => item.code === code)
    if (target) {
      target.stock = stock
      if (stock <= 0 && target.status === 'SELLING') target.status = 'SOLDOUT'
      if (stock > 0 && target.status === 'SOLDOUT') target.status = 'SELLING'
    }
  }

  const remove = async (code: string) => {
    await deleteSellerProduct(code)
    products.value = products.value.filter((item) => item.code !== code)
  }

  const sellingCount = computed(() => products.value.filter((item) => item.status === 'SELLING').length)

  return {
    products,
    loading,
    loaded,
    errorMessage,
    sellingCount,
    load,
    upsert,
    changeStatus,
    changeStock,
    remove,
  }
}
